import { ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { ReactNode } from "react";

export interface DrawerNavMenuItemProps {
  icon: ReactNode;
  label: string;
  tooltip: string;
  path: string;
  collapsed?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  openInNewTab?: boolean;
}

/**
 * Navigation menu item component.
 *
 * Displays an icon, label, and tooltip. Highlights when the current route matches the path.
 * When `openInNewTab` is true, renders as an external anchor that opens in a new tab and
 * shows a pop-out icon.
 */
export function DrawerNavMenuItem({
  icon,
  label,
  tooltip,
  path,
  collapsed = false,
  disabled = false,
  disabledTooltip,
  openInNewTab = false,
}: DrawerNavMenuItemProps) {
  const location = useLocation();
  // Never mark external-tab entries as active — they don't navigate the parent app.
  const isActive =
    !disabled &&
    !openInNewTab &&
    (path === "/" ? location.pathname === "/" : location.pathname.startsWith(path));

  const tooltipTitle = disabled ? disabledTooltip || tooltip : collapsed ? tooltip : "";

  let buttonProps: Record<string, unknown> = {};
  if (!disabled) {
    if (openInNewTab) {
      buttonProps = {
        component: "a",
        href: path,
        target: "_blank",
        rel: "noopener noreferrer",
      };
    } else {
      buttonProps = { component: Link as React.ElementType, to: path };
    }
  }

  return (
    <ListItem disablePadding disableGutters sx={{ height: 48 }}>
      <Tooltip title={tooltipTitle} placement="right">
        <ListItemButton
          {...buttonProps}
          selected={isActive}
          disabled={disabled}
          disableGutters
          sx={{
            borderRadius: 0.5,
            padding: 1,
            minWidth: 48,
            justifyContent: collapsed ? "center" : "flex-start",
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 24,
              marginRight: collapsed ? 0 : 1.5,
              justifyContent: "center",
              color: disabled ? "text.disabled" : isActive ? "primary.main" : "text.secondary",
            }}
          >
            {icon}
          </ListItemIcon>
          {!collapsed && (
            <ListItemText
              primary={label}
              sx={{
                "& span": {
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                },
              }}
            />
          )}
          {!collapsed && openInNewTab && (
            <OpenInNew
              sx={{
                fontSize: 16,
                color: disabled ? "text.disabled" : "text.secondary",
                ml: 1,
              }}
            />
          )}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
}
